/**
 * 自社の広告の枠の口を呼ぶ所（ここだけ）。約束: docs/teams/money/self-serve-ads-contract.md §2（作り手）・§3（運営）。
 * サーバーは ssa-core（ブランチ money/self-serve-ads）が作る。形を変えたくなったら約束を先に（ここで勝手に変えない）。
 *
 * 約束への要望（docs/teams/mgmt/tasks/ssa-dash-result.md）の口は 2026-10-22 ssa-finish でサーバーにできた（約束 §2・§3 に追記済み）:
 *   - POST …/campaigns/:id/suggest … AI の案（一文 + 2 つのバナー）を作って広告に入れる（要望 1）
 *   - GET  …/campaigns/:id/banners/:size（作り手）・GET /v1/staff/…/campaigns/:id/banners/:size（運営）… バナーの画像そのもの（要望 2）
 *   古いサーバー（口が無い）では 404 route_missing が返り、画面は落ち着いた案内を出す（そのまま残す）
 *
 * フラグ off（KUMO_SELF_SERVE_ADS=off）では作り手・運営の口が 404 not_found（口があることも見せない）= isOff() で「出さない」に読む。
 */
import { api, API_BASE, ApiError, getToken } from './api';
import type { Campaign, CampaignDetail, CheckedTarget, HostPref, HostSource, HouseHostPref, SelfAdSettings, StaffCampaign } from './selfAds';

const BASE = '/v1/money/self-ads';
const STAFF = '/v1/staff/money/self-ads';

/** フラグ off（404 not_found）か、このサーバーにまだ口が無い（route_missing）= 画面ごと出さない */
export function isOff(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404 && (e.code === 'not_found' || e.code === 'route_missing');
}

export const SETTINGS_PATH = `${BASE}/settings`;
export const CAMPAIGNS_PATH = `${BASE}/campaigns`;
/** 広告にできる自分のアプリの一覧（約束 §2・2026-10-22 ssa-finish） */
export const TARGETS_PATH = `${BASE}/targets`;
export const campaignPath = (id: string) => `${BASE}/campaigns/${encodeURIComponent(id)}`;
export const hostPrefPath = (projectId: string) => `${BASE}/hosts/${encodeURIComponent(projectId)}`;

export const checkUrl = (url: string) => api<{ ok: true; target: CheckedTarget }>(`${BASE}/check-url`, { method: 'POST', body: { url } });

export const createCampaign = (body: { url: string; dailyCapCents?: number; totalCapCents?: number | null }) =>
  api<{ campaign: Campaign }>(CAMPAIGNS_PATH, { method: 'POST', body: stripNullTotal(body) });

export const updateCampaign = (
  id: string,
  body: { tagline?: string; banner320AssetId?: string | null; banner728AssetId?: string | null; dailyCapCents?: number; totalCapCents?: number | null }
) => api<{ campaign: Campaign }>(campaignPath(id), { method: 'PATCH', body });

/**
 * AI の案（約束 §2・2026-10-22 ssa-finish でサーバーに口ができた）。hint = 作り手の「ひとこと」（無くてもよい）。原価は AI の利用料として前払いから。
 * AI の利用規約の同意は求めない（2026-10-22 ssa-founder-1022・founder「AI の利用規約はいらない」= 以前の acceptTerms と同意のチェックは外した）
 */
export const suggestCreative = (id: string, hint?: string) =>
  api<{ campaign: Campaign; costCents?: number }>(`${campaignPath(id)}/suggest`, { method: 'POST', body: hint ? { hint } : {} });

/**
 * 自分のバナーを上げる（2026-10-22 ssa-founder-1022・founder「アップロードでもいい」）。本文 = 画像そのもの（ブラウザで枠の形に切り抜いた物）。
 * 形式はサーバーが中身の頭で決める（content-type は見ない）= ここは application/octet-stream のまま送る（api の raw）
 */
export const uploadBanner = (id: string, size: 320 | 728, bytes: Uint8Array) =>
  api<{ campaign: Campaign }>(`${campaignPath(id)}/banners/${size}`, { method: 'PUT', raw: bytes });

export const submitCampaign = (id: string) => api<{ campaign: Campaign }>(`${campaignPath(id)}/submit`, { method: 'POST', body: {} });
export const pauseCampaign = (id: string) => api<{ campaign: Campaign }>(`${campaignPath(id)}/pause`, { method: 'POST', body: {} });
export const resumeCampaign = (id: string) => api<{ campaign: Campaign }>(`${campaignPath(id)}/resume`, { method: 'POST', body: {} });

export const getHostPref = (projectId: string) => api<HostPref>(hostPrefPath(projectId));
export const putHostPref = (projectId: string, body: { source?: HostSource; notified?: boolean }) =>
  api<HostPref>(hostPrefPath(projectId), { method: 'PUT', body });

/** 旗 house の「KUMO の広告を出す / 出さない」（帯・リワード）。旗 house でないサーバー・範囲 internal で社内でない作り手（2026-10-25 money/house-internal）は 404 not_found = isOff() */
export const houseHostPrefPath = (projectId: string) => `${BASE}/house-hosts/${encodeURIComponent(projectId)}`;
export const getHouseHostPref = (projectId: string) => api<HouseHostPref>(houseHostPrefPath(projectId));
export const putHouseHostPref = (projectId: string, body: { banner?: boolean; rewarded?: boolean }) =>
  api<HouseHostPref>(houseHostPrefPath(projectId), { method: 'PUT', body });

export const getCampaignDetail = (id: string) => api<CampaignDetail>(campaignPath(id));
export const getSettings = () => api<SelfAdSettings>(SETTINGS_PATH);

// 総額の上限なし（null）は送らない = 約束の POST は `totalCapCents?`（null を受けるとは書いていない）。PATCH は null で「上限を外す」に使う（約束への要望 3）
function stripNullTotal<T extends { totalCapCents?: number | null }>(b: T): T {
  if (b.totalCapCents === null) {
    const { totalCapCents: _drop, ...rest } = b;
    return rest as T;
  }
  return b;
}

// ------------------------------------------------------------------ バナーの画像（約束への要望 2）

/**
 * バナーの画像を読んで <img> に渡せる data: の URL にする。WHY fetch か: 画像の口も鍵（kds_ / 運営の鍵）が要り、<img src> はヘッダを送れない。
 * 鍵を URL に入れない（ログ・履歴に残る）。
 * WHY blob: でなく data: か: ダッシュボードの CSP（csp.ts の img-src）は 'self' data: だけを許す。blob: を足すと CSP を広げることになる =
 *   バナー 2 枚（数十 KB）なら data: で足りるので、CSP は変えない
 */
export async function bannerDataUrl(campaignId: string, size: 320 | 728, staffToken?: string): Promise<string> {
  const path = `${staffToken ? STAFF : BASE}/campaigns/${encodeURIComponent(campaignId)}/banners/${size}`;
  const token = staffToken ?? getToken();
  let res: Response;
  try {
    // no-store: 作り直すと同じ道で別の画像になる（古い画像をキャッシュから出さない）
    res = await fetch(`${API_BASE}${path}`, { cache: 'no-store', headers: token ? { authorization: `Bearer ${token}` } : {} });
  } catch {
    throw new ApiError(0, 'network_error', 'network');
  }
  if (!res.ok) throw new ApiError(res.status, res.status === 404 ? 'route_missing' : 'http_error', `HTTP ${res.status}`);
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new ApiError(0, 'client_error', 'read'));
    fr.readAsDataURL(blob);
  });
}

// ------------------------------------------------------------------ 運営（約束 §3）

/**
 * 運営の口は作り手のログイン（kds_）ではなく運営の鍵（KUMO_STAFF_TOKEN）。auth: false = 作り手の鍵を付けない・401 でログアウトさせない。
 * 鍵は画面のメモリにだけ置く（localStorage に入れない = 共有の端末に残さない。pages/StaffAdsReviewPage.tsx）
 */
const staff = <T>(token: string, path: string, init: { method?: string; body?: unknown } = {}) =>
  api<T>(`${STAFF}${path}`, { ...init, auth: false, headers: { authorization: `Bearer ${token}` } });

export const staffReviews = (token: string) => staff<{ campaigns: StaffCampaign[] }>(token, '/reviews');
export const staffApprove = (token: string, id: string, note?: string) =>
  staff<{ campaign: Campaign }>(token, `/campaigns/${encodeURIComponent(id)}/approve`, { method: 'POST', body: note ? { note } : {} });
export const staffReject = (token: string, id: string, reasons: string[], note?: string) =>
  staff<{ campaign: Campaign }>(token, `/campaigns/${encodeURIComponent(id)}/reject`, { method: 'POST', body: note ? { reasons, note } : { reasons } });
